import React, { useEffect, useState, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import PricingTimeZoneField, { validPricingTimeZone } from './PricingTimeZoneField';
import { getPricingDraft, savePricingDraft, pricingLoadError, PricingPolicy } from '../../../services/individualPricingSettings';
import { fromSouthAfricanDateTimeInputValue, toSouthAfricanDateTimeInputValue } from '../../../utils/southAfricanTime';

const supportedCalculationRules = 'ZA-NCR-2006-2015-REVIEW-2026-09-13-UNAPPROVED';

const choices: Record<string, string[][]> = {
  dayCount: [['ACTUAL_MONTH', 'Actual days in the month'], ['THIRTY', '30-day month']],
  balanceConvention: [['DAILY_AVERAGE', 'Average daily balance'], ['AGREEMENT_TIME', 'Balance at an agreed time']],
  postingRounding: [['HALF_UP', 'Round half up'], ['HALF_EVEN', 'Round half to even'], ['DOWN', 'Round down']],
  initiationTreatment: [['SEPARATE_UPFRONT', 'Paid separately upfront'], ['FINANCED', 'Added to the loan balance'], ['DEDUCTED_FROM_PAYOUT', 'Deducted from cash paid out']],
  repaymentMethod: [['EQUAL_PRINCIPAL', 'Equal principal — variable payments'], ['LEVEL_PAYMENT_FINAL_ADJUSTMENT', 'Level payments — final adjustment']],
  paymentDayTiming: [['BEFORE_SCHEDULED_REPAYMENT', 'Before the scheduled repayment'], ['AFTER_SCHEDULED_REPAYMENT', 'After the scheduled repayment']],
  tax: [['TAXABLE', 'Tax applies'], ['NOT_APPLICABLE', 'Tax does not apply']],
};
const fields = [
  ['firstMonthlyPercent', 'First-loan monthly interest (%)', 'number'],
  ['subsequentMonthlyPercent', 'Subsequent-loan monthly interest (%)', 'number'],
  ['monthlyServiceFeeBase', 'Monthly service fee before tax (R)', 'number'],
  ['dayCount', 'Interest day-count method', 'select'],
  ['balanceConvention', 'Balance used for interest', 'select'],
  ['postingRounding', 'Rounding method', 'select'],
  ['repaymentMethod', 'Repayment method', 'select'],
  ['paymentDayTiming', 'Balance on repayment day', 'select'],
  ['initiationTreatment', 'How the initiation fee is paid', 'select'],
];
// Display guidance mirrors the current draft validator; not configurable regulatory rules or approval.
const fieldGuidance: Record<string, string> = {
  firstMonthlyPercent: 'Current draft range: 0–5%; up to 4 decimal places. Enter 5 for 5%, not 0.05.',
  subsequentMonthlyPercent: 'Current draft range: 0–3%; up to 4 decimal places. Enter 3 for 3%, not 0.03.',
  monthlyServiceFeeBase: 'Current draft range: R0–R60 before tax; up to 2 decimal places.',
};
export const pricingFieldLabel = (field: string) => Object.fromEntries(fields.map(([key, label]) => [key, label]))[field]
  || ({ displayName: 'Pricing name', ruleBundleId: 'Regulatory rules', effectiveFrom: 'Effective date', balanceTime: 'Agreed balance time',
    balanceZone: 'Time zone', reason: 'Reason for this change', revision: 'Draft revision', initiationTax: 'Initiation-fee tax', serviceTax: 'Service-fee tax', maximumCapacityUtilisationPercent: 'Maximum capacity utilisation (%)',
    minimumPostRepaymentSurplus: 'Minimum surplus after repayment (R)', policy: 'Policy approval' } as Record<string,string>)[field]
  || (field.startsWith('initiationTax.') ? 'Initiation-fee tax details' : field.startsWith('serviceTax.') ? 'Service-fee tax details' : 'Pricing configuration');

export type PricingLeaveGuard = (() => boolean) | null;
export default function IndividualPricingEditor({ id, onClose, onLeaveGuardChange }: {
  id?: string; onClose: () => void; onLeaveGuardChange?: (guard: PricingLeaveGuard) => void;
}) {
  const [policy, setPolicy] = useState<PricingPolicy>();
  const [effectiveInput, setEffectiveInput] = useState('');
  const [effectiveDate = '', effectiveTime = ''] = effectiveInput.split('T');
  const [persisted, setPersisted] = useState(!!id);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [locked, setLocked] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [retry, setRetry] = useState(0);
  const feedback = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error || message) {
      feedback.current?.focus();
      feedback.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }
  }, [error, message]);
  useEffect(() => {
    let active = true;
    if (!id) {
      setPolicy({ policyId: crypto.randomUUID(), revision: 1,
        ruleBundleId: supportedCalculationRules });
      setEffectiveInput('');
      return;
    }
    getPricingDraft(id).then(draft => { if (active) {
      setPolicy(draft.policy); setMissing((draft.validation?.issues ?? []).map(i => pricingFieldLabel(i.field))); setError('');
      setEffectiveInput(draft.policy.effectiveFrom ? toSouthAfricanDateTimeInputValue(draft.policy.effectiveFrom) : '');
    } }).catch(e => { if (active) setError(pricingLoadError(e)); });
    return () => { active = false; };
  }, [id, retry]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty || busy) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy]);
  useEffect(() => {
    onLeaveGuardChange?.(() => !busy && (!dirty || window.confirm('Discard unsaved pricing changes?')));
    return () => onLeaveGuardChange?.(null);
  }, [dirty, busy, onLeaveGuardChange]);
  const change = (key: string, value: unknown) => {
    setPolicy(p => p && ({ ...p, [key]: value,
      ...(key === 'balanceConvention' && value !== 'AGREEMENT_TIME' ? { balanceTime: null, balanceZone: null } : {}) }));
    setDirty(true); setMessage('');
  };
  const taxChange = (key: string, field: string, value: unknown) =>
    change(key, { ...(policy?.[key] as object || {}), [field]: value });
  const changeEffective = (date: string, time: string) => {
    setEffectiveInput(`${date}T${time}`);
    change('effectiveFrom', date && time ? fromSouthAfricanDateTimeInputValue(`${date}T${time}`) : null);
  };
  const applyStartingSettings = () => {
    const effectiveFrom = new Date(Date.now() + 30 * 60 * 1000).toISOString();
    setEffectiveInput(toSouthAfricanDateTimeInputValue(effectiveFrom));
    setPolicy(previous => previous && ({ ...previous,
      ruleBundleId: supportedCalculationRules,
      effectiveFrom,
      firstMonthlyPercent: '5', subsequentMonthlyPercent: '3',
      monthlyServiceFeeBase: '0',
      dayCount: 'ACTUAL_MONTH', balanceConvention: 'DAILY_AVERAGE',
      balanceTime: null, balanceZone: null, postingRounding: 'HALF_UP',
      initiationTreatment: 'FINANCED',
      repaymentMethod: 'LEVEL_PAYMENT_FINAL_ADJUSTMENT', paymentDayTiming: 'BEFORE_SCHEDULED_REPAYMENT',
      initiationTax: undefined,
      serviceTax: { treatment: 'NOT_APPLICABLE', percent: '0', evidenceReference: 'No service fee charged' },
    }));
    setDirty(true); setError('');
    setMessage('Starting settings applied. Review the values and save this draft; approval and activation are separate.');
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault(); if (!policy || busy || locked) return;
    if (!!effectiveDate !== !!effectiveTime) {
      setMessage(''); setError(`Choose ${effectiveDate ? 'a time (SAST)' : 'an effective date'} before saving. Your entries are retained.`); return;
    }
    if (policy.balanceConvention === 'AGREEMENT_TIME' && !validPricingTimeZone(String(policy.balanceZone ?? ''))) {
      setMessage(''); setError('Choose a recognised time zone before saving. Your entries are retained.'); return;
    }
    setBusy(true); setError(''); setMessage('');
    try {
      const nextPolicy: PricingPolicy = { ...policy, revision: persisted ? policy.revision + 1 : 1 };
      delete nextPolicy.initiationFeeBase;
      const result = await savePricingDraft(nextPolicy,
        reason.trim(), persisted ? policy.revision : undefined);
      setPolicy(result.policy); setPersisted(true); setDirty(false); setReason('');
      setEffectiveInput(result.policy.effectiveFrom ? toSouthAfricanDateTimeInputValue(result.policy.effectiveFrom) : '');
      setMissing((result.validation?.issues ?? []).map(i => pricingFieldLabel(i.field)));
      setMessage(`Draft saved as revision ${result.policy.revision}. Live pricing is unchanged.`);
    } catch (e) {
      const status = (e as { response?: { status: number } }).response?.status;
      if (status === 400) {
        const issues = (e as { response?: { data?: { issues?: { field: string; message: string }[] } } }).response?.data?.issues;
        const details = Array.isArray(issues) ? issues.filter(i => typeof i?.field === 'string' && typeof i?.message === 'string')
          .map(i => `${pricingFieldLabel(i.field)}: ${i.message}`).join('; ') : '';
        setError(details ? `${details}. Your entries are retained.`
          : 'Some supplied values are invalid. Check rates, fees, tax details and calculation choices. Your entries are retained.');
      }
      else if (status === 403) setError('Your account does not have permission to save pricing drafts. Your entries are retained.');
      else { setLocked(true); setError(status === 409
        ? 'This draft changed or already exists. Reload the saved draft before making further changes.'
        : 'Save could not be confirmed. Check the saved draft before retrying; your entries are retained.'); }
    } finally { setBusy(false); }
  };
  const reloadSaved = async () => {
    if (!policy || !window.confirm('Replace your entries with the latest saved draft?')) return;
    setBusy(true);
    try {
      const result = await getPricingDraft(policy.policyId);
      setPolicy(result.policy); setPersisted(true); setDirty(false); setLocked(false); setReason(''); setError('');
      setEffectiveInput(result.policy.effectiveFrom ? toSouthAfricanDateTimeInputValue(result.policy.effectiveFrom) : '');
      setMissing((result.validation?.issues ?? []).map(i => pricingFieldLabel(i.field))); setMessage('Latest saved revision loaded.');
    } catch (e) {
      if (!persisted && (e as { response?: { status: number } }).response?.status === 404) {
        setLocked(false); setError('No saved draft was found. Your entries are retained; you can retry saving.');
      } else setError(pricingLoadError(e));
    } finally { setBusy(false); }
  };
  const close = () => { if (!busy && (!dirty || window.confirm('Discard unsaved changes and return to the list?'))) onClose(); };
  const money = (cents: number) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);
  return <section>
    <button className="btn btn-outline-secondary btn-sm mb-3" disabled={busy} onClick={close}>Back to drafts</button>
    <h3 className="h5">{persisted ? `Edit draft · revision ${policy?.revision ?? ''}` : 'New pricing draft'}</h3>
    <p>Save your proposed settings here. Blank choices are allowed in a draft. Saving does not approve or activate pricing.</p>
    <p className="small text-muted">Calculation options still need verified support before publication. Regulatory limits cannot be changed here.</p>
    <p className="small text-muted">The ranges below describe current draft validation, not recommended charges or approval. Leave a field blank if it is not yet decided.</p>
    {!policy ? <button className="btn btn-outline-secondary" onClick={() => setRetry(n => n + 1)}>Retry loading draft</button> :
      <form onSubmit={save}>
        <fieldset disabled={busy || locked}>
          <button type="button" className="btn btn-outline-primary mb-2" onClick={applyStartingSettings}>Use starting settings</button>
          <p className="small text-muted">Uses the short-term interest ceilings of 5% for a first loan and 3% for subsequent loans in the calendar year, the calculated initiation fee and no service fee.
            You can choose lower interest rates. Review and complete initiation-fee tax details before activation. The effective time is set 30 minutes ahead.
            This does not save or activate pricing.</p>
          <div className="row g-3">
            <div className="col-12"><label htmlFor="pricing-name" className="form-label">Pricing name (optional)</label>
              <input id="pricing-name" className="form-control" maxLength={80} value={String(policy.displayName ?? '')}
                placeholder="e.g. October 2026 standard pricing" onChange={event => change('displayName', event.target.value || null)} />
              <p className="form-text">Saved with this draft and used as its default name at activation.</p>
            </div>
            <div className="col-md-6">
              <label htmlFor="pricing-rules" className="form-label">Calculation rules</label>
              <input id="pricing-rules" className="form-control bg-light" readOnly
                value={policy.ruleBundleId === supportedCalculationRules ? 'South African short-term credit' : 'Update required'} />
              {policy.ruleBundleId !== supportedCalculationRules && <div className="alert alert-warning mt-2 mb-0">
                <p className="mb-2">This draft needs the current calculation rules before it can be activated. Update the rules and save a new revision.</p>
                <button type="button" className="btn btn-outline-primary btn-sm"
                  onClick={() => change('ruleBundleId', supportedCalculationRules)}>Use current calculation rules</button>
              </div>}
            </div>
            <div className="col-md-6">
              <div className="row g-2">
                <div className="col-sm-6"><label htmlFor="pricing-effective-date" className="form-label">Effective date</label>
                  <input id="pricing-effective-date" className="form-control" type="date" required={!!effectiveTime}
                    aria-describedby="pricing-effective-help" value={effectiveDate}
                    onChange={e => changeEffective(e.target.value, effectiveTime)} /></div>
                <div className="col-sm-6"><label htmlFor="pricing-effective-time" className="form-label">Time (SAST)</label>
                  <input id="pricing-effective-time" className="form-control" type="time" required={!!effectiveDate}
                    aria-describedby="pricing-effective-help" value={effectiveTime}
                    onChange={e => changeEffective(effectiveDate, e.target.value)} /></div>
              </div>
              <p id="pricing-effective-help" className="form-text">Enter South African time (SAST, UTC+02:00). The confirmation uses the same time. Must still be in the future when you activate.</p>
            </div>
            {fields.map(([key, label, type]) => <div className="col-md-6" key={key}>
              <label htmlFor={`pricing-${key}`} className="form-label">{label}</label>
              {type === 'select' ? <select id={`pricing-${key}`} className="form-select" value={String(policy[key] ?? '')}
                onChange={e => change(key, e.target.value || null)}>
                <option value="">Not selected</option>{choices[key].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select> : <input id={`pricing-${key}`} className="form-control" type="number" min="0" step="any"
                aria-describedby={`pricing-${key}-help`}
                value={String(policy[key] ?? '')} onChange={e => change(key, e.target.value || null)} />}
              {fieldGuidance[key] && <p id={`pricing-${key}-help`} className="form-text mb-0">{fieldGuidance[key]}</p>}
            </div>)}
            <fieldset className="col-12 border-top pt-3"><legend className="h6">Initiation fee examples</legend>
              <p className="small text-muted">The initiation fee is calculated automatically from the loan amount. Before tax, use the lowest of R165 plus 10% of the portion above R1,000, R1,050, and 15% of the loan amount. No fee amount needs to be entered.</p>
                <div className="table-responsive"><table className="table table-sm mb-2">
                  <caption>Initiation fee examples before tax</caption>
                  <thead><tr><th scope="col">Loan amount</th><th scope="col">Calculated initiation fee</th></tr></thead>
                  <tbody>{[500, 1000, 1500, 2350, 3000, 3500, 4000, 8000].map(principal => {
                    // Illustrative whole-rand amounts: calculate in cents; actual quotes come from the server.
                    const ceilingCents = Math.min(16500 + Math.max(principal - 1000, 0) * 10, 105000, principal * 15);
                    return <tr key={principal}><th scope="row">{money(principal * 100)}</th>
                      <td>{money(ceilingCents)}</td></tr>;
                  })}</tbody>
                </table></div>
              <p className="small text-muted mb-0">Tax is applied to the calculated fee using the tax settings below. Existing quotations and agreements keep their recorded fees.</p>
            </fieldset>
            {policy.balanceConvention === 'AGREEMENT_TIME' && <>
              <div className="col-md-6"><label htmlFor="pricing-time">Agreed balance time</label><input id="pricing-time" className="form-control" type="time"
                value={String(policy.balanceTime ?? '')} onChange={e => change('balanceTime', e.target.value || null)} /></div>
              <PricingTimeZoneField value={String(policy.balanceZone ?? '')} onChange={value => change('balanceZone', value || null)} />
            </>}
            <fieldset className="col-12 border-top pt-3"><legend className="h6">Individual affordability limits</legend>
              <p className="small text-muted">Both limits must pass. These are lender policy settings; they do not replace the required affordability checks.</p>
              <div className="row g-3">{[
                ['maximumCapacityUtilisationPercent', '100', 'Largest repayment as a percentage of available monthly capacity after assessed expenses, maintenance and existing debt. Greater than 0%, up to 100%, with two decimal places. Blank preserves the existing 100% limit.'],
                ['minimumPostRepaymentSurplus', '0', 'Minimum rand amount that must remain from monthly capacity after the largest repayment. Non-negative, with two decimal places. Blank preserves the existing R0 minimum.'],
              ].map(([key, fallback, help]) => <div className="col-md-6" key={key}>
                <label htmlFor={`pricing-${key}`} className="form-label">{pricingFieldLabel(key)}</label>
                <button type="button" className="btn btn-link p-0 ms-1 text-muted" aria-label={`Help: ${pricingFieldLabel(key)}`}
                  data-tooltip-id={`pricing-${key}-help`} data-tooltip-content={help}><i className="fa fa-question-circle" aria-hidden="true" /></button>
                <Tooltip id={`pricing-${key}-help`} />
                <input id={`pricing-${key}`} className="form-control" type="number" min={key === 'maximumCapacityUtilisationPercent' ? '0.01' : '0'}
                  max={key === 'maximumCapacityUtilisationPercent' ? '100' : undefined} step="0.01" placeholder={fallback}
                  value={String(policy[key] ?? '')} onChange={event => change(key, event.target.value || null)} />
              </div>)}</div>
              <p className="form-text mb-0">Blank values retain 100% utilisation and R0 surplus. Saving is a draft only. Activated limits apply to quotations from that version;
                explicitly requote and reassess a pending application to use a newer version.</p>
            </fieldset>
            {['initiationTax', 'serviceTax'].map(key => {
              const tax = (policy[key] || {}) as Record<string,unknown>;
              return <fieldset className="col-12 border-top pt-3" key={key}><legend className="h6">{pricingFieldLabel(key)}</legend>
                <div className="row g-3">{[['treatment','Tax treatment'],['percent','Tax rate (%)'],['evidenceReference','Supporting approval/reference']].map(([field,label]) =>
                  <div className="col-md-4" key={field}><label className="form-label" htmlFor={`${key}-${field}`}>{label}</label>
                    {field === 'treatment' ? <select id={`${key}-${field}`} className="form-select" value={String(tax[field] ?? '')}
                      onChange={e => taxChange(key, field, e.target.value || null)}><option value="">Not selected</option>
                      {choices.tax.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select>
                    : <input id={`${key}-${field}`} className="form-control" type={field === 'percent' ? 'number' : 'text'} min="0" step="any"
                      value={String(tax[field] ?? '')} onChange={e => taxChange(key, field, e.target.value || null)} />}
                  </div>)}</div>
                <p className="small text-muted mt-2">A reference records your basis; it does not itself verify tax approval. No tax rate is assumed.</p>
              </fieldset>;
            })}
            <div className="col-12"><label htmlFor="pricing-reason" className="form-label">Reason for this change</label>
              <textarea id="pricing-reason" className="form-control" required value={reason} onChange={e => { setReason(e.target.value); setDirty(true); }} /></div>
          </div>
          <button className="btn btn-primary mt-3" type="submit" disabled={!reason.trim()}>{busy ? 'Saving…' : 'Save draft'}</button>
        </fieldset>
        {(error || message) && <div ref={feedback} tabIndex={-1} className={`alert mt-3 ${error ? 'alert-danger' : 'alert-success'}`}
          role={error ? 'alert' : 'status'}>{error || message}</div>}
        {locked && <button type="button" className="btn btn-outline-secondary mt-3" disabled={busy} onClick={reloadSaved}>Check latest saved draft</button>}
        {missing.length > 0 && <div className="alert alert-warning mt-3 mb-0" role="status">
          <strong>Draft not ready for publication</strong>
          <p className="mb-1">Outstanding checks: {[...new Set(missing)].join(', ')}.</p>
          {missing.includes('Regulatory rules') && <p className="small mb-0">
            The regulatory basis is an internal reference, not an interest-rate field. Complete the visible calculation inputs, then preview and activate this pricing revision through the normal workflow.
          </p>}
        </div>}
      </form>}
    {!policy && error && <div ref={feedback} tabIndex={-1} role="alert" className="alert alert-danger">{error}</div>}
  </section>;
}
